import { useLocale } from '@app/core/hooks/useLocale';
import EquityBridgeBaseValueRow from './EquityBridgeBaseValueRow';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { EquityBridgeColumnRole } from '@app/shared/models/contracts/enums/shared-enums';
import { EquityBridgeColumnDto } from '@app/shared/models/contracts/project-dto';
import classNames from 'classnames';
import styles from './equity-bridge-content.module.scss';
import ColumnControls from '@app/shared/components/table/ColumnControls';
import { useEquityBridgeTable } from './utils/useEquityBridgeTable';
import { roleLabel } from './utils/equityBridgeTableHelper';
import { dateFormatter } from '@app/shared/formatters/date-formatter';
import EquityBridgeCommonNetDebtItems from './EquityBridgeCommonNetDebtItems';

interface EquityBridgeTableProps {
  onChange: () => void;
  investmentDate: string;
  valuationDate: string;
}

const EquityBridgeTable = ({ onChange, investmentDate, valuationDate }: EquityBridgeTableProps) => {
  const {
    columns,
    commonNetDebtItems,
    addCommonDebtItem,
    shiftColumn,
    removeCommonDebtItem,
    removeColumn,
    canShift,
    isDefaultColumn,
  } = useEquityBridgeTable(onChange);

  const { l } = useLocale();

  const dateFormatterProps = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  } as Intl.DateTimeFormatOptions;

  const getColumnDateLabel = (column: EquityBridgeColumnDto): string => {
    switch (column.role) {
      case EquityBridgeColumnRole.Investment:
        return dateFormatter(investmentDate, dateFormatterProps);
      case EquityBridgeColumnRole.Valuation:
        return dateFormatter(valuationDate, dateFormatterProps);
      default:
        return '';
    }
  };

  return (
    <table className={classNames('table-primary table-primary--zebra', styles['table'])}>
      <colgroup>
        <col className={styles['label-col']} />
        {columns.map((column) => (
          <col key={column.columnId} className={styles['data-col']} />
        ))}
        <col className="table-primary__col-action" />
      </colgroup>
      <thead>
        <tr>
          <th
            className="table-primary__cell--header table-primary__cell--vertical-separator"
            aria-hidden
          />
          {columns.map((column, columnIndex) =>
            isDefaultColumn(column) ? (
              <th
                key={column.columnId}
                className="table-primary__cell--header table-primary__cell--right">
                {roleLabel(column)}
              </th>
            ) : (
              <ColumnControls
                key={column.columnId}
                onDelete={removeColumn(column.columnId)}
                onShift={(direction) => shiftColumn(column.columnId, direction)()}
                canShift={(direction) => canShift(columnIndex, direction)}
              />
            )
          )}
          <th className="table-primary__cell--header" aria-hidden />
        </tr>
        <tr className="table-primary__row--header">
          <th
            className="table-primary__cell--strong table-primary__cell--vertical-separator"
            aria-hidden
          />
          {columns.map((column, columnIndex) =>
            isDefaultColumn(column) ? (
              <th
                key={column.columnId}
                className="table-primary__cell--strong table-primary__cell--right">
                {getColumnDateLabel(column)}
              </th>
            ) : (
              <th
                key={column.columnId}
                className="table-primary__cell--strong table-primary__cell--center">
                <GridTextField
                  name={`columns.${columnIndex}.dateMemo`}
                  alignment={Alignment.Center}
                  isInputMasked={false}
                  placeholder={l('_AddMemoTitle')}
                  className="table-primary__cell--strong table-primary__cell--center"
                />
              </th>
            )
          )}
          <th className="table-primary__cell--strong" aria-hidden />
        </tr>
      </thead>
      <tbody>
        <EquityBridgeBaseValueRow
          label={l('_EquityValue')}
          fieldKey="equityValue"
          columns={columns}
        />
        <EquityBridgeBaseValueRow label={l('_LessCash')} fieldKey="cashValue" columns={columns} />
        <EquityBridgeCommonNetDebtItems
          commonNetDebtItems={commonNetDebtItems}
          columns={columns}
          removeCommonDebtItem={removeCommonDebtItem}
          addCommonDebtItem={addCommonDebtItem}
        />
        <EquityBridgeBaseValueRow
          label={l('_EnterpriseValue')}
          fieldKey="enterpriseValue"
          columns={columns}
        />
      </tbody>
    </table>
  );
};

export default EquityBridgeTable;
